import { useState, useCallback, useRef } from "react";
import _ from "lodash";

function useErrors() {
  const [errors, setErrors] = useState({});

  const set = useCallback(
    (key, message) =>
      setErrors((prevErrors) => ({ ...prevErrors, [key]: message })),
    [setErrors]
  );

  const reset = useCallback(() => {
    if (!_.isEmpty(errors)) {
      setErrors({});
    }
  }, [errors, setErrors]);

  return [errors, set, reset];
}

const defaultValidator = (currentVal, initialVal) =>
  !_.isEqual(currentVal, initialVal);

export function useForm(initialState = {}, validators = {}) {
  const [state, setState] = useState(initialState);
  const [errors, setError, resetErrors] = useErrors();

  const stateKeys = useRef(Object.keys(initialState));

  const set = useCallback(
    (name, value) => {
      setState((values) => ({
        ...values,
        [name]: value,
      }));
    },
    [setState]
  );

  const getValidator = (key) => validators[key] || defaultValidator;

  const didValueChange = (key) => {
    const validator = getValidator(key);

    return validator(state[key], initialState[key]);
  };

  const getChanges = () => {
    return stateKeys.current.reduce((changes, key) => {
      const hasChanges = didValueChange(key);

      return !hasChanges
        ? changes
        : {
            ...changes,
            [key]: state[key],
          };
    }, {});
  };

  return {
    set,
    values: state,
    errors,
    didFormChange: () => stateKeys.current.some(didValueChange),
    didValueChange,
    getChanges,
    discardChanges: () => setState(initialState),
    setError,
    resetErrors,
    hasErrors: () => _.some(errors, Boolean),
  };
}
